import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Dropdown, Input, Modal, Select, Table, Tooltip, message } from "antd";
import {
  CheckCircle2,
  MoreVertical,
  PauseCircle,
  Pencil,
  PlayCircle,
  Plus,
  Search,
  Sparkles,
  Trash2,
  XCircle,
} from "lucide-react";

import { AN_COLORS, AN_FONT, AN_MONO, anAlpha } from "../../../design-system/tokens";
import { DOMAIN_ID, ORG_ID } from "../../../constants";
import {
  approvePolicyAgentless,
  getAllADDomains,
  listADPolicies,
  policyAction,
  revokePolicyAgentless,
} from "../../../service/adPolicyService";
import { deletePolicyJSON } from "../../../service/appService";
import {
  ActionPill,
  AuthTypeChip,
  ModeBadge,
  RetiredTag,
  ScopeTag,
  StatusPill,
  TargetCell,
  deriveScope,
  deriveTargets,
} from "./adPolicyTokens";
import {
  domainFqdn,
  domainLabel,
  domainPosture,
  isRetiredAction,
  // aliased: policyAction is also the name of the PolicyAction API call above
  policyAction as effectiveAction,
} from "./adPolicyModel";
import DiscoverFromADDrawer from "./DiscoverFromADDrawer";

// The order the engine evaluates actions in: a matching Allow ends evaluation,
// then Deny, and anything still matching gets a step-up prompt. Rows sort by
// this so the flat list still reads in evaluation order.
const ACTION_ORDER = ["allow", "block", "mfa_required"];

const ACTION_FILTERS = [
  { value: "allow", label: "Allow" },
  { value: "block", label: "Deny" },
  { value: "mfa_required", label: "MFA Required" },
];

function policyPriority(row) {
  const p = row?.priority ?? row?.policyJson?.priority;
  return typeof p === "number" ? p : Number.POSITIVE_INFINITY;
}

function formatUpdated(ts) {
  if (!ts) return "—";
  try {
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  } catch {
    return "—";
  }
}

export default function AdPoliciesPage() {
  const navigate = useNavigate();
  const [messageApi, messageContext] = message.useMessage();
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [domains, setDomains] = useState([]);
  const [search, setSearch] = useState("");
  const [domainFilter, setDomainFilter] = useState(null);
  const [actionFilter, setActionFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [discoverDrawerOpen, setDiscoverDrawerOpen] = useState(false);
  const [modalApi, modalContext] = Modal.useModal();

  const fetchPolicies = useCallback(() => {
    setLoading(true);
    listADPolicies({
      payload: {
        orgId: ORG_ID(),
        tenantId: DOMAIN_ID(),
        policyType: "AD",
        page: 1,
        limit: 200,
      },
      onSuccess: (res) => {
        const list = (res?.policies || res?.data || []).filter(
          (p) => (p?.policyType || "").toLowerCase() === "ad"
        );
        setPolicies(list);
        setLoading(false);
      },
      onFailure: () => {
        messageApi.error("Failed to load AD policies");
        setLoading(false);
      },
    });
  }, [messageApi]);

  const fetchDomains = useCallback(() => {
    getAllADDomains({
      payload: { orgId: ORG_ID(), tenantId: DOMAIN_ID() },
      onSuccess: (res) => setDomains(res?.domains || res?.data || []),
      onFailure: () => {},
    });
  }, []);

  useEffect(() => {
    fetchPolicies();
    fetchDomains();
  }, [fetchPolicies, fetchDomains]);

  const visiblePolicies = useMemo(() => {
    const q = search.trim().toLowerCase();
    return policies.filter((p) => {
      const json = p?.policyJson || {};
      if (q && !(p.policyName || "").toLowerCase().includes(q)) return false;
      if (domainFilter && json?.ad?.domain !== domainFilter) return false;
      if (actionFilter && effectiveAction(json) !== actionFilter) return false;
      if (statusFilter && p.status !== statusFilter) return false;
      return true;
    });
  }, [policies, search, domainFilter, actionFilter, statusFilter]);

  // One flat list, still ordered the way the engine evaluates: by action first,
  // then by priority within an action (lower wins).
  const rows = useMemo(
    () =>
      [...visiblePolicies].sort((a, b) => {
        const byAction =
          ACTION_ORDER.indexOf(effectiveAction(a.policyJson)) -
          ACTION_ORDER.indexOf(effectiveAction(b.policyJson));
        if (byAction !== 0) return byAction;
        return policyPriority(a) - policyPriority(b);
      }),
    [visiblePolicies],
  );

  // Enforcement is a property of the domain, so each row reads it off the
  // domain it targets rather than off the policy.
  const postureFor = useCallback(
    (row) => {
      const ad = row?.policyJson?.ad || {};
      const domain =
        (domains || []).find((d) => d.id === ad.adId) ||
        (domains || []).find(
          (d) => domainFqdn(d) === ad.domain || domainLabel(d) === ad.domain,
        );
      return domainPosture(domain);
    },
    [domains],
  );

  const appliedFilters = [
    domainFilter && { key: "domain", label: `Domain · ${domainFilter}`, clear: () => setDomainFilter(null) },
    actionFilter && { key: "action", label: `Action · ${actionFilter}`, clear: () => setActionFilter(null) },
    statusFilter && { key: "status", label: `Status · ${statusFilter}`, clear: () => setStatusFilter(null) },
  ].filter(Boolean);

  const clearFilters = () => {
    setDomainFilter(null);
    setActionFilter(null);
    setStatusFilter(null);
    setSearch("");
  };

  const handleDeletePolicy = (row) => {
    const id = row.id || row.policyId;
    if (!id) {
      messageApi.error("Missing policy id — cannot delete");
      return;
    }
    modalApi.confirm({
      title: `Delete policy "${row.policyName}"?`,
      content: "This cannot be undone. The policy will stop being evaluated immediately.",
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: () =>
        new Promise((resolve) => {
          deletePolicyJSON({
            payload: {
              orgId: ORG_ID(),
              tenantId: DOMAIN_ID(),
              policyId: id,
            },
            onSuccess: () => {
              messageApi.success(`Policy "${row.policyName}" deleted`);
              fetchPolicies();
              resolve();
            },
            onFailure: () => {
              messageApi.error("Failed to delete policy");
              resolve();
            },
          });
        }),
    });
  };

  const handleUpdatePolicy = (row) => {
    const id = row.id || row.policyId;
    if (!id) {
      messageApi.error("Missing policy id — cannot edit");
      return;
    }
    navigate(`/pam/policy/v2/edit/${id}`, { state: { policy: row } });
  };

  const handleChangeStatus = (row, actionValue) => {
    const id = row.id || row.policyId;
    if (!id) {
      messageApi.error("Missing policy id");
      return;
    }
    const successMsg =
      actionValue === "Approved" ? "Policy re-activated" : "Policy paused";
    const failureVerb = actionValue === "Approved" ? "re-activate" : "pause";
    policyAction({
      payload: {
        orgId: ORG_ID(),
        tenantId: DOMAIN_ID(),
        action: {
          actionType: "status",
          actionIds: [id],
          actionValue,
        },
      },
      onSuccess: () => {
        messageApi.success(successMsg);
        fetchPolicies();
      },
      onFailure: () => messageApi.error(`Failed to ${failureVerb} policy`),
    });
  };

  const handleApprovePolicy = (row) => {
    const id = row.id || row.policyId;
    if (!id) {
      messageApi.error("Missing policy id");
      return;
    }
    approvePolicyAgentless({
      payload: { orgId: ORG_ID(), tenantId: DOMAIN_ID(), policyId: id },
      onSuccess: () => {
        messageApi.success(`Policy "${row.policyName}" approved`);
        fetchPolicies();
      },
      onFailure: () => messageApi.error("Failed to approve policy"),
    });
  };

  const handleRevokePolicy = (row) => {
    const id = row.id || row.policyId;
    if (!id) {
      messageApi.error("Missing policy id");
      return;
    }
    modalApi.confirm({
      title: `Revoke policy "${row.policyName}"?`,
      content:
        "The policy will stop being evaluated until it's approved again.",
      okText: "Revoke",
      okButtonProps: { danger: true },
      onOk: () =>
        new Promise((resolve) => {
          revokePolicyAgentless({
            payload: { orgId: ORG_ID(), tenantId: DOMAIN_ID(), policyId: id },
            onSuccess: () => {
              messageApi.success(`Policy "${row.policyName}" revoked`);
              fetchPolicies();
              resolve();
            },
            onFailure: () => {
              messageApi.error("Failed to revoke policy");
              resolve();
            },
          });
        }),
    });
  };

  const columns = [
    {
      title: "Policy name",
      key: "policyName",
      render: (_, row) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontFamily: AN_FONT, fontWeight: 500, color: AN_COLORS.ink }}>
            {row.policyName}
          </span>
          {isRetiredAction(row.policyJson?.policyFlow) && <RetiredTag />}
          {row.policyJson?.policyKind === "risk_based" && (
            <Tooltip title="Created with risk-based evaluation, which the evaluator doesn't run. It evaluates as a static policy.">
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  padding: "1px 6px",
                  background: "#F5EFFB",
                  color: "#7C3AED",
                  border: "1px solid #E2D2F5",
                  borderRadius: 4,
                  fontSize: 10,
                  fontFamily: AN_MONO,
                  fontWeight: 600,
                  letterSpacing: 0.4,
                }}
              >
                RISK
              </span>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      // Was a section header per action; now a column, so the evaluation
      // outcome is readable per row without splitting the list.
      title: "Action",
      key: "action",
      width: 130,
      render: (_, row) => <ActionPill action={effectiveAction(row.policyJson)} />,
    },
    {
      title: "Scope",
      key: "scope",
      width: 90,
      render: (_, row) => <ScopeTag scope={deriveScope(row.policyJson)} />,
    },
    {
      title: "AD groups / users",
      key: "targets",
      render: (_, row) => (
        <TargetCell
          targets={deriveTargets(row.policyJson)}
          scope={deriveScope(row.policyJson)}
        />
      ),
    },
    {
      title: "Auth",
      key: "auth",
      width: 110,
      render: (_, row) => {
        const types = row.policyJson?.authTypes || [];
        return (
          <span style={{ display: "inline-flex", gap: 4 }}>
            {types.map((t) => (
              <AuthTypeChip key={t} type={t} />
            ))}
          </span>
        );
      },
    },
    {
      title: "Enforcement",
      key: "enforcement",
      width: 110,
      render: (_, row) => <ModeBadge mode={postureFor(row) || "unknown"} />,
    },
    {
      title: "Status",
      key: "status",
      width: 110,
      render: (_, row) => <StatusPill status={row.status} />,
    },
    {
      title: "Updated",
      key: "updated",
      width: 170,
      render: (_, row) => (
        <span style={{ fontFamily: AN_MONO, fontSize: 12, color: AN_COLORS.muted, fontVariantNumeric: "tabular-nums" }}>
          {formatUpdated(row.updatedAt)}
        </span>
      ),
    },
    {
      title: "",
      key: "actions",
      width: 44,
      align: "center",
      render: (_, row) => {
        const isApproved = row.status === "Approved";
        const items = [
          {
            key: "update",
            icon: <Pencil size={13} />,
            label: "Update policy",
            onClick: () => handleUpdatePolicy(row),
          },
        ];
        if (!isApproved) {
          items.push({
            key: "approve",
            icon: <CheckCircle2 size={13} />,
            label: "Approve",
            onClick: () => handleApprovePolicy(row),
          });
        }
        if (isApproved) {
          items.push({
            key: "pause",
            icon: <PauseCircle size={13} />,
            label: "Pause",
            onClick: () => handleChangeStatus(row, "Suspended"),
          });
        } else if (row.status === "Suspended") {
          items.push({
            key: "reactivate",
            icon: <PlayCircle size={13} />,
            label: "Re-activate",
            onClick: () => handleChangeStatus(row, "Approved"),
          });
        }
        if (isApproved) {
          items.push({
            key: "revoke",
            icon: <XCircle size={13} />,
            label: "Revoke",
            onClick: () => handleRevokePolicy(row),
          });
        }
        items.push({ type: "divider" });
        items.push({
          key: "delete",
          danger: true,
          icon: <Trash2 size={13} />,
          label: "Delete policy",
          onClick: () => handleDeletePolicy(row),
        });
        return (
          <Dropdown trigger={["click"]} placement="bottomRight" menu={{ items }}>
            <button
              type="button"
              aria-label="Row actions"
              style={{
                width: 28,
                height: 28,
                border: "none",
                background: "transparent",
                color: AN_COLORS.muted,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 6,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <MoreVertical size={16} />
            </button>
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div
      style={{
        minHeight: "calc(100vh - 64px)",
        background: AN_COLORS.bgWarm,
        color: AN_COLORS.ink,
        fontFamily: AN_FONT,
        padding: "22px 28px",
      }}
    >
      {messageContext}
      {modalContext}

      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          marginBottom: 18,
          gap: 16,
        }}
      >
        <div>
          <div
            style={{
              fontFamily: AN_MONO,
              fontSize: 10.5,
              letterSpacing: 1.4,
              textTransform: "uppercase",
              color: AN_COLORS.muted,
              marginBottom: 6,
            }}
          >
            Admin · Policies · AD
          </div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600, letterSpacing: -0.4 }}>
            Policies
          </h1>
          <div style={{ fontSize: 13, color: AN_COLORS.muted, marginTop: 4 }}>
            Manage MFA and access policies for Active Directory users, groups, and OUs.
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
            <CountChip
              count={policies.length}
              label={policies.length === 1 ? "Policy" : "Policies"}
            />
            {domains.length > 0 && (
              <CountChip
                count={domains.length}
                label={domains.length === 1 ? "Domain" : "Domains"}
              />
            )}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Button
            icon={<Sparkles size={13} />}
            onClick={() => setDiscoverDrawerOpen(true)}
          >
            Discover from AD
          </Button>
          <Button
            type="primary"
            icon={<Plus size={13} strokeWidth={2.2} />}
            onClick={() => navigate("/pam/policy/v2/create")}
          >
            New policy
          </Button>
        </div>
      </div>

      {/* Filter bar */}
      <div
        style={{
          background: "var(--an-surface)",
          border: `1px solid ${AN_COLORS.line}`,
          borderRadius: 10,
          padding: 14,
          marginBottom: 14,
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1.6fr 1fr 1fr 1fr",
            gap: 12,
            alignItems: "end",
          }}
        >
          <FilterField label="Search by name">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              prefix={<Search size={14} color={AN_COLORS.muted} />}
              placeholder="Domain Admins, jdoe…"
              style={{ height: 36 }}
            />
          </FilterField>
          <FilterField label="Domain">
            <Select
              value={domainFilter}
              onChange={setDomainFilter}
              allowClear
              placeholder="Any domain"
              style={{ width: "100%", height: 36 }}
              options={domains.map((d) => ({
                value: d.domainName || d.directoryName,
                label: d.directoryName || d.domainName,
              }))}
            />
          </FilterField>
          <FilterField label="Action">
            <Select
              value={actionFilter}
              onChange={setActionFilter}
              allowClear
              placeholder="Any action"
              style={{ width: "100%", height: 36 }}
              options={ACTION_FILTERS}
            />
          </FilterField>
          <FilterField label="Status">
            <Select
              value={statusFilter}
              onChange={setStatusFilter}
              allowClear
              placeholder="Any status"
              style={{ width: "100%", height: 36 }}
              options={[
                { value: "Approved", label: "Active" },
                { value: "Suspended", label: "Paused" },
              ]}
            />
          </FilterField>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginTop: 12,
            flexWrap: "wrap",
          }}
        >
          <span style={{ fontSize: 12.5, color: AN_COLORS.ink2, fontWeight: 500 }}>
            Applied:
          </span>
          {appliedFilters.length === 0 ? (
            <span style={{ fontSize: 12.5, color: AN_COLORS.muted }}>None</span>
          ) : (
            appliedFilters.map((f) => (
              <AppliedChip key={f.key} onClear={f.clear}>
                {f.label}
              </AppliedChip>
            ))
          )}
          {appliedFilters.length > 0 && (
            <button
              type="button"
              onClick={clearFilters}
              style={{
                border: "none",
                background: "transparent",
                color: AN_COLORS.primary,
                fontSize: 12.5,
                fontWeight: 500,
                cursor: "pointer",
                padding: 0,
              }}
            >
              Clear filters
            </button>
          )}
          <span
            style={{
              marginLeft: "auto",
              fontSize: 12,
              color: AN_COLORS.muted,
              fontFamily: AN_MONO,
            }}
          >
            Showing {visiblePolicies.length} of {policies.length}
          </span>
        </div>
      </div>

      {/* Table */}
      <div
        style={{
          background: "var(--an-surface)",
          border: `1px solid ${AN_COLORS.line}`,
          borderRadius: 10,
          overflow: "hidden",
        }}
      >
        <Table
          rowKey={(row) => row.id || row.policyId || row.policyName}
          columns={columns}
          dataSource={rows}
          loading={loading}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          scroll={{ x: 1080 }}
          locale={{
            emptyText: loading
              ? "Loading…"
              : policies.length === 0
                ? "No AD policies yet."
                : "No policies match the current filters.",
          }}
        />
      </div>

      <DiscoverFromADDrawer
        open={discoverDrawerOpen}
        onClose={() => setDiscoverDrawerOpen(false)}
        domains={domains}
        onApplied={() => {
          setDiscoverDrawerOpen(false);
          fetchPolicies();
        }}
      />
    </div>
  );
}

function CountChip({ count, label }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "baseline",
        gap: 6,
        padding: "3px 10px",
        background: AN_COLORS.surface,
        border: `1px solid ${AN_COLORS.line}`,
        borderRadius: 99,
        fontSize: 12.5,
        color: AN_COLORS.muted,
        whiteSpace: "nowrap",
      }}
    >
      <span
        style={{
          fontFamily: AN_MONO,
          fontWeight: 600,
          color: AN_COLORS.ink,
          fontVariantNumeric: "tabular-nums",
          fontSize: 13,
        }}
      >
        {count.toLocaleString()}
      </span>
      <span style={{ fontWeight: 500 }}>{label}</span>
    </span>
  );
}

function FilterField({ label, children }) {
  return (
    <label style={{ display: "block" }}>
      <div
        style={{
          fontSize: 11.5,
          fontWeight: 500,
          color: AN_COLORS.ink2,
          marginBottom: 6,
          fontFamily: AN_FONT,
        }}
      >
        {label}
      </div>
      {children}
    </label>
  );
}

function AppliedChip({ children, onClear }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "4px 4px 4px 10px",
        background: AN_COLORS.primaryL,
        color: AN_COLORS.primary,
        borderRadius: 99,
        fontSize: 12,
        fontWeight: 500,
        border: `1px solid ${anAlpha(AN_COLORS.primary, 13)}`,
      }}
    >
      {children}
      <button
        type="button"
        onClick={onClear}
        aria-label="Remove filter"
        style={{
          width: 16,
          height: 16,
          borderRadius: "50%",
          border: "none",
          background: "transparent",
          color: AN_COLORS.primary,
          cursor: "pointer",
          padding: 0,
          fontSize: 14,
          lineHeight: 1,
        }}
      >
        ×
      </button>
    </span>
  );
}
